import { Head, useForm } from "@inertiajs/react";
import { Download, Upload } from "lucide-react";
import SchoolLayout from "@/components/schools/SchoolLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import ImportReport, { CsvFileInput } from "@/components/schools/ImportReport";
import { GuideTip } from "@/components/schools/setup/SetupPrimitives";
import { pluralize } from "@/lib/format";

export default function TeachersImport({ school, membership, campaign, template_path, import_path, back, report }) {
  // The controller reads the plain `file` param (see TeacherImportsController#create).
  const { setData, post, processing, progress } = useForm({ file: null });

  const submit = (e) => {
    e.preventDefault();
    post(import_path, { forceFormData: true });
  };

  return (
    <SchoolLayout school={school} membership={membership} eyebrow={campaign.name} title="Invite teachers" back={back}>
      <Head title={`${campaign.name} · Invite teachers`} />

      <div className="space-y-6">
        <ImportReport report={report} />

        <Card>
          <CardHeader>
            <CardTitle>Invite teachers from a spreadsheet</CardTitle>
            <CardDescription>
              Each teacher gets one email with a sign-in link, however many classrooms they have.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">1. Download the template</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">
                It already lists your {pluralize(campaign.classroom_count, "classroom")}. Add each teacher's name and email beside their classroom.
              </p>
              <Button variant="outline" asChild>
                <a href={template_path} download>
                  <Download className="h-4 w-4" />
                  Download the template
                </a>
              </Button>
            </section>

            <form onSubmit={submit} className="space-y-5 border-t border-border pt-6" encType="multipart/form-data">
              <h3 className="text-sm font-semibold text-foreground">2. Upload your filled-in file</h3>
              <CsvFileInput
                label="Teachers (CSV)"
                onChange={(file) => setData("file", file)}
                columns="classroom, teacher_name, teacher_email"
              />
              {progress && <p className="text-sm text-muted-foreground">Uploading… {progress.percentage}%</p>}
              <Button type="submit" disabled={processing}>
                <Upload className="h-4 w-4" />
                {processing ? "Inviting…" : "Invite teachers"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <GuideTip title="Good to know">
          <ul className="list-disc space-y-1.5 pl-5">
            <li>No teacher for a classroom yet? Leave its row blank.</li>
            <li>A teacher with more than one classroom: repeat their email on each row.</li>
            <li>Uploading again adds new teachers and never removes anyone.</li>
          </ul>
        </GuideTip>
      </div>
    </SchoolLayout>
  );
}
